import { useIntl } from 'react-intl';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { IdentityProvidersMessages } from '../../IdentityProviders.messages';
import { externalIdpLogoutConfigSchema } from 'pages/IdentityProviders/schema/identityProviderSchema';
import { OidcAuthFlow } from '../../../../api-client';

type UseCreateOidcProviderForm = () => UseFormReturn;

export const useCreateOidcProviderForm: UseCreateOidcProviderForm = () => {
  const intl = useIntl();
  const schema = yup.object().shape(
    {
      name: yup.string().required(intl.formatMessage(IdentityProvidersMessages.nameRequiredError)),
      clientId: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.clientIdRequiredError)),
      userType: yup
        .string()
        .oneOf(['Staff', 'Shoppers'])
        .required(intl.formatMessage(IdentityProvidersMessages.userTypeRequiredError)),
      externalIdField: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.externalIdRequiredError))
        .oneOf(['Uid', 'Email', 'PhoneNumber']),
      type: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.userTypeRequiredError)),
      externalIdpLogoutConfig: externalIdpLogoutConfigSchema,
      issuer: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.issuerRequiredError))
        .url(intl.formatMessage(IdentityProvidersMessages.issuerUrlValidationError)),
      prompt: yup
        .string()
        .notRequired()
        .nullable()
        .when('prompt', {
          is: (value: string | undefined | null) => !!value && value?.length,
          then: (rules) =>
            rules.oneOf(
              ['none', 'login', 'consent', 'select_account'],
              'Prompt must be one of: none, login, consent, select_account ',
            ),
        }),
      flow: yup
        .string()
        .required('Flow is required')
        .oneOf([OidcAuthFlow.Implicit, OidcAuthFlow.Code]),
      usePKCE: yup.boolean(),
      clientSecret: yup.string().when('flow', {
        is: OidcAuthFlow.Code,
        then: (schema) => schema.required('Client secret is required when flow is Code'),
        otherwise: (schema) => schema.notRequired(),
      }),
    },
    [['prompt', 'prompt']],
  );

  return useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
};
